import { useFormik } from "formik";
import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdAddCircleOutline } from "react-icons/md";

import { ReactComponent as EyeIcon } from "../../../assets/eye-closed.svg";
import { ReactComponent as EyeSlashIcon } from "../../../assets/eye-open.svg";
import { useSnack } from "../../../hooks/useSnack";
import schoolServices from "../../../services/schoolServices";
import studentServices from "../../../services/studentServices";
import { getLanguage } from "../../../utils/getLanguage";
import { addStudentModalValidationSchema } from "../../../utils/validationSchema";
import Button from "../../Button";
import Modal from "../../Modal";

const TextInput = ({ name, label, type = "text", placeholder, helpText, formik }) => (
  <div className="relative">
    <div className="mb-1 flex items-end space-x-1">
      <label className="text-sm font-medium text-mutedText dark:text-dark-mutedText">{label}</label>
      {helpText && (
        <div className="group relative mt-[-5px]">
          <span className="cursor-pointer rounded-full bg-black px-[4px] text-[10px] text-white dark:text-gray-500">
            ?
          </span>
          <div className="absolute top-5 z-10 hidden w-[260px] rounded-md bg-gray-800 p-2 text-xs text-white shadow-md group-hover:block ltr:left-0 rtl:right-0">
            <p className="ltr:left-0 rtl:right-0">{helpText}</p>
          </div>
        </div>
      )}
      {formik.touched[name] && (formik.errors[name] === "Required" || formik.errors[name] === "مطلوب") && (
        <span className="text-start text-xl leading-3 text-red-300">*</span>
      )}
    </div>
    <input
      name={name}
      type={type}
      placeholder={placeholder}
      className="block w-full rounded-md border bg-[#F9FAFB] px-3 py-2 text-sm placeholder:text-mutedText focus:border-green-500 focus:ring focus:ring-green-500 focus:ring-opacity-10 dark:border-dark-background dark:bg-dark-background dark:text-white"
      value={formik.values[name]}
      onChange={formik.handleChange}
      onBlur={formik.handleBlur}
    />
    {formik.touched[name] && formik.errors[name] !== "Required" && formik.errors[name] !== "مطلوب" && (
      <p className="mt-1 text-start text-xs text-red-300">{formik.errors[name]}</p>
    )}
  </div>
);

const PasswordInput = ({ name, label, placeholder, formik, isRtl, visibility, toggleVisibility }) => (
  <div>
    <div className="mb-1 flex items-end space-x-1">
      <label className="block text-sm font-medium text-mutedText dark:text-dark-mutedText">{label}</label>
      {formik.touched[name] && (formik.errors[name] === "Required" || formik.errors[name] === "مطلوب") && (
        <span className="text-start text-xl leading-3 text-red-300">*</span>
      )}
    </div>
    <div className="relative">
      <input
        name={name}
        type={visibility ? "text" : "password"}
        placeholder={placeholder}
        className="block w-full rounded-md border bg-[#F9FAFB] px-3 py-2 text-sm placeholder:text-mutedText focus:border-green-500 focus:ring focus:ring-green-500 focus:ring-opacity-10 dark:border-dark-background dark:bg-dark-background dark:text-white"
        value={formik.values[name]}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        autoComplete="new-password"
      />
      <span
        className={`absolute ${isRtl ? "left-2" : "right-2"} top-1/2 -translate-y-1/2 cursor-pointer`}
        onClick={toggleVisibility}
      >
        {visibility ? (
          <EyeSlashIcon className="h-4 w-4 text-mutedText dark:text-dark-mutedText" />
        ) : (
          <EyeIcon className="h-4 w-4 text-mutedText dark:text-dark-mutedText" />
        )}
      </span>
    </div>
    {formik.touched[name] && formik.errors[name] !== "Required" && formik.errors[name] !== "مطلوب" && (
      <p className="mt-1 text-start text-xs text-red-300">{formik.errors[name]}</p>
    )}
  </div>
);

const ImageUpload = ({ name, label, formik }) => {
  const handleFileChange = (event) => {
    const file = event.currentTarget.files[0];
    formik.setFieldValue(name, file);
  };

  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-mutedText dark:text-dark-mutedText">
        {label}
      </label>
      <input
        type="file"
        name={name}
        accept="image/*"
        className="block w-full text-sm text-gray-500 file:mr-4 file:rounded-md file:border-0 file:bg-green-500 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-green-600"
        onChange={handleFileChange}
      />
      {formik.errors[name] && <p className="mt-1 text-start text-xs text-red-300">{formik.errors[name]}</p>}
    </div>
  );
};

const AddStudentModal = forwardRef(({ onSubmit }, ref) => {
  const isThirdPartyUser = false;
  const lang = getLanguage();
  const isRtl = lang === "ar";
  const { t } = useTranslation();
  const { showSnack } = useSnack();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [selectedClassroom, setSelectedClassroom] = useState(null);
  const [limit, setLimit] = useState("");

  useImperativeHandle(ref, () => ({
    openModal(selectedClassroom) {
      setSelectedClassroom(selectedClassroom);

      setIsOpen(true);
    },
    closeModal() {
      setIsOpen(false);
    },
  }));

  const togglePasswordVisibility = () => setPasswordVisible(!passwordVisible);
  const toggleConfirmPasswordVisibility = () => setConfirmPasswordVisible(!confirmPasswordVisible);

  const formik = useFormik({
    initialValues: {
      name: "",
      username: "",
      password: "",
      confirmPassword: "",
      ...(isThirdPartyUser && { email: "" }),
    },
    validationSchema: addStudentModalValidationSchema(t, isThirdPartyUser),
    onSubmit: async (values, { resetForm }) => {
      setIsSubmitting(true);
      try {
        const payload = new FormData();
        payload.append("name", values.name);
        payload.append("username", values.username);
        payload.append("classroom_id", selectedClassroom.id);
        payload.append("password", values.password);
        payload.append("password_confirmation", values.confirmPassword);
        if (isThirdPartyUser) {
          payload.append("b12_email", values.email);
        }
        const response = await studentServices.addNewStudent(payload);
        showSnack(response.message, "success");
        onSubmit(values);
        resetForm();
        setIsOpen(false);
        setTimeout(() => {
          setIsSubmitting(false);
        }, 500);
      } catch (error) {
        showSnack(error, "error");
        setIsSubmitting(false);
      }
    },
  });

  const handleCloseModal = () => {
    formik.resetForm();
    setIsOpen(false);
  };

  const fetchGenerateUsername = async () => {
    const res = await studentServices.generateUsername(formik.values.name);
    formik.setFieldValue("username", res?.username);
  };

  const getLimit = async () => {
    const res = await schoolServices.getSchoolStudentsLimitation();
    setLimit(res);
  };

  useEffect(() => {
    if (isOpen) {
      getLimit();
    }
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCloseModal}
      header={
        <div className="flex items-center justify-between">
          <p className="flex items-center gap-2 text-green-500">
            <MdAddCircleOutline size={22} />
            {t("add_new_student")}
          </p>
          <p className="text-xs font-light text-mutedText dark:text-dark-mutedText">
            <span className="font-medium">{limit?.current_students_count}</span> {t("student_accounts_limit")}{" "}
            {t("out_of")} <span className="font-medium">{limit?.school_limitation}</span>
          </p>
        </div>
      }
      footer={
        <div className="flex items-center justify-end space-x-2">
          <Button label={t("cancel")} onClick={handleCloseModal} color="white" variant="outlined" />
          <Button
            className="w-36"
            color="green"
            disabled={isSubmitting}
            loading={isSubmitting}
            onClick={formik.handleSubmit}
            label={t("create")}
          />
        </div>
      }
    >
      <form>
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-x-4 gap-y-3 md:grid-cols-1">
            <TextInput
              name="name"
              label={t("student_name")}
              placeholder={t("student_name")}
              formik={formik}
            />
            <div className="flex items-center justify-between space-x-2">
              <div className="flex-1">
                <TextInput
                  name="username"
                  label={t("username")}
                  placeholder={t("username")}
                  helpText={t("firstName_3_Digit_Random_Number")}
                  formik={formik}
                />
              </div>
              <Button
                disabled={formik.values.name === ""}
                type="button"
                className="mt-5"
                onClick={fetchGenerateUsername}
                color="green"
                label={t("generate_username")}
              />
            </div>
            <PasswordInput
              name="password"
              label={t("password")}
              placeholder={t("password")}
              formik={formik}
              isRtl={isRtl}
              visibility={passwordVisible}
              toggleVisibility={togglePasswordVisibility}
            />
            <PasswordInput
              name="confirmPassword"
              label={t("confirm_password")}
              placeholder={t("confirm_password")}
              formik={formik}
              isRtl={isRtl}
              visibility={confirmPasswordVisible}
              toggleVisibility={toggleConfirmPasswordVisibility}
            />
            {isThirdPartyUser && (
              <TextInput
                type="email"
                name="email"
                label={t("b12_email")}
                placeholder={t("b12_email")}
                formik={formik}
              />
            )}
            {/* <ImageUpload name="image" label={t("upload_profile_picture")} formik={formik} /> */}
          </div>
        </div>
      </form>
    </Modal>
  );
});

export default AddStudentModal;
